<template>
    <div class="redesign-columns-manage">
        <div class="px-4 pt-4">
            <div class=" text-sm mb-2">
                {{ t('widget.table.manage.showTable') }}
            </div>
            <VcRadioButtonsGroup
                v-model="labelType"
                class="mb-7 w-max"
                dir="ltr"
                :radios="Object.values(tableHeaderGlobalOptions)"
                :config="{
                    valueKey: 'label',
                    labelKey: 'visibleLabel',
                }"
            />
            <div
                v-if="labelType === tableHeaderGlobalOptions.shortText.label
                    || labelType === tableHeaderGlobalOptions.fullText.label"
                class="flex gap-2 mb-4"
            >
                <VcSwitch v-model="iconsVisibility" />
            </div>
            <div
                v-show="labelType === tableHeaderGlobalOptions.custom.label"
                class="mb-4"
            >
                <VcButton
                    type="outline"
                    @click="regenerateColumnsConfig"
                >
                    {{ t('widget.table.manage.generateWidthAutomatically') }}
                </VcButton>
            </div>
            <div class="mb-3 text-gray-550 text-xs uppercase">
                {{ t('widget.table.manage.showVisibleColumn') }}
            </div>
            <div class="flex flex-col justify-between gap-4">
                <div class="flex-grow">
                    <VcInput
                        v-model="searchColumnInput"
                        :prefix-icon="'Search'"
                        :placeholder="`${t('general.search')}`"
                    />
                </div>
                <div class="w-auto flex-shrink-0">
                    <div class="column-item">
                        <VcCheckbox
                            :model-value="isAllCheckboxChecked"
                            :indeterminate="isAllIndeterminate"
                            @update:modelValue="selectAllColumns"
                        >
                            {{ t('common.showAll') }}
                        </VcCheckbox>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="manage-block px-4 mb-4 py-4 flex-1"
        >
            <div v-if="hiddenSearchColumns.length">
                <div
                    v-for="(column, i) in hiddenSearchColumns"
                    :key="column.prop + i"
                    class="column-item"
                    :class="{'column-item--active' : column.highlight }"
                >
                    <VcCheckbox
                        class="text-sm"
                        :model-value="false"
                        @change="addToVisible(column.prop)"
                    >
                        {{ getManageColumnDisplayLabel(column) }}
                    </VcCheckbox>
                </div>
            </div>
            <draggable
                :list="visibleColumnsRef"
                @end="onEnd"
            >
                <div
                    v-for="(column, i) in searchedVisibleColumns"
                    :key="column.prop + 'column' + i"
                    class="column-item column-item--draggable"
                    :class="{'column-item--active' : column.highlight }"
                >
                    <div class="flex items-center gap-2 flex-1 min-w-0">
                        <VcCheckbox
                            v-if="!isEditingColumn(column.prop)"
                            :model-value="true"
                            @change="addToHidden(column.prop)"
                        >
                            {{ getManageColumnDisplayLabel(column) }}
                        </VcCheckbox>
                        <div
                            v-else
                            class="flex items-center gap-2 flex-1"
                        >
                            <VcCheckbox
                                :model-value="true"
                                disabled
                                class="!w-auto"
                            />
                            <VcInput
                                :ref="(el: ComponentPublicInstance | null) => setColumnInputRef(column.prop, el)"
                                :model-value="getTempEditingValue(column.prop)"
                                class="column-edit-input flex-1"
                                @update:model-value="updateEditingValue(column.prop, $event)"
                                @keyup.enter="saveColumnLabel(column)"
                                @keyup.esc="cancelEditingColumn(column.prop)"
                            />
                            <div class="flex items-center gap-1 flex-shrink-0">
                                <VcButtonIcon
                                    icon="Check"
                                    color="primary"
                                    class="column-edit-apply-btn"
                                    @click.stop="saveColumnLabel(column)"
                                />
                                <VcButtonIcon
                                    icon="X"
                                    color="secondary"
                                    class="column-edit-cancel-btn"
                                    @click.stop="cancelEditingColumn(column.prop)"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="flex items-center gap-1 flex-shrink-0">
                        <VcButtonIcon
                            v-if="isCustomMode && !isEditingColumn(column.prop) && hasCustomLabel(column.prop)"
                            icon="RotateCcw"
                            color="secondary"
                            class="column-reset-btn"
                            @click.stop="resetColumnLabel(column.prop)"
                        />
                        <VcButtonIcon
                            v-if="isCustomMode && !isEditingColumn(column.prop)"
                            icon="vc-icon-edit-pencil"
                            color="secondary"
                            class="column-edit-btn"
                            @click.stop="startEditingColumn(column)"
                        />
                        <RedesignBaseIcon :name="'GripVertical'" />
                    </div>
                </div>
            </draggable>
            <div v-if="hiddenUnSearchColumns.length">
                <div
                    v-for="(column, i) in hiddenUnSearchColumns"
                    :key="column.prop + i"
                    class="column-item"
                >
                    <VcCheckbox
                        :model-value="false"
                        @change="addToVisible(column.prop)"
                    >
                        {{ getManageColumnDisplayLabel(column) }}
                    </VcCheckbox>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import { VcSwitch, VcButton, VcButtonIcon, VcInput, VcCheckbox, VcRadioButtonsGroup } from '@voicenter-team/voicenter-ui-plus'
import { VueDraggableNext as draggable } from 'vue-draggable-next'
import { computed, nextTick, reactive, ref, watch } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import cloneDeep from 'lodash/cloneDeep'
import { getTableHeaderGlobalOptions } from '@/enum/redesignTableHeaderOptions'
import { ColumnsObjectConfig, TableHeadersConfig, WidgetTemplateColumnWithConfig } from '@/types/dashboard'

const { t } = useI18n()

//Props
const props = withDefaults(
    defineProps<{
        visibleColumns: WidgetTemplateColumnWithConfig[]
        hiddenColumns: WidgetTemplateColumnWithConfig[]
        columnsConfig: ColumnsObjectConfig
        globalHeadersConfig: TableHeadersConfig

    }>(),
    {
        visibleColumns: () => [],
        hiddenColumns: () => [],
        columnsConfig: () => ({}),
    }
)

//Emits
const emit = defineEmits<{
    (event: 'change-icons-visibility', payload: boolean): void
    (event: 'change-label', payload: string): void
    (event: 'update-columns-config', payload: ColumnsObjectConfig): void
    (event: 'regenerate-columns-config'): void
}>()

//State
const columnsConfigRef = ref<ColumnsObjectConfig>({})
const iconsVisibility = ref(false)
const visibleColumnsRef = ref<WidgetTemplateColumnWithConfig[]>([])
const labelType = ref('')
const isSyncingGlobalHeadersConfig = ref(false)
const searchColumnInput = ref('')
const editingColumnProp = ref<string | null>(null)
const editingColumnValues = reactive<Record<string, string>>({})
const tempEditingValues = ref<Record<string, string>>({})
const originalColumnValues = reactive<Record<string, string>>({})
const columnInputRefs = ref<Record<string, ComponentPublicInstance | null>>({})

//Computed
// Get table header options with translated labels at runtime
const tableHeaderGlobalOptions = computed(() => getTableHeaderGlobalOptions())

const searchedVisibleColumns = computed(() => {
    if (!searchColumnInput.value) {
        return visibleColumnsRef.value
    }
    return visibleColumnsRef.value.map(col => {
        if (!col.label) {
            return col
        }
        if (col.label.fullTextLabel.toLowerCase().includes(searchColumnInput.value.toLowerCase())) {
            return {
                ...col,
                highlight: true
            }
        }
        return col
    })
})

const isAllCheckboxChecked = computed(() => {
    return props.visibleColumns.length > 0 && !isAllIndeterminate.value
})
const isAllIndeterminate = computed(() => {
    return (props.visibleColumns.length > 0 && props.hiddenColumns.length > 0)
})

const hiddenSearchColumns = computed(() => {
    if (!searchColumnInput.value) {
        return []
    }
    return props.hiddenColumns
        .filter((column) => column.label.fullTextLabel
            .toLowerCase()
            .includes(searchColumnInput.value.toLowerCase())
        )
})

const hiddenUnSearchColumns = computed(() => {
    if (!searchColumnInput.value) {
        return props.hiddenColumns
    }
    return props.hiddenColumns.filter((column) => !column.label.fullTextLabel.toLowerCase().includes(searchColumnInput.value.toLowerCase()))
})

const isCustomMode = computed(() => {
    return labelType.value === tableHeaderGlobalOptions.value.custom.label
})

const MAX_DISPLAY_LABEL_LENGTH = 15

function truncateLabel (text: string, maxLength: number): string {
    if (!text || text.length <= maxLength) return text
    return `${text.slice(0, maxLength)}...`
}

function getColumnDisplayLabel (column: WidgetTemplateColumnWithConfig): string {
    if (isEditingColumn(column.prop)) {
        return editingColumnValues[column.prop] || ''
    }
    if (isCustomMode.value) {
        const config = columnsConfigRef.value[column.prop]
        if (config?.customTextLabel) {
            return truncateLabel(config.customTextLabel, MAX_DISPLAY_LABEL_LENGTH)
        }
        return column.label?.fullTextLabel || column.label?.toString() || ''
    }
    if (labelType.value === tableHeaderGlobalOptions.value.shortText.label) {
        return column.label?.shortTextLabel || column.label?.fullTextLabel || column.label?.toString() || ''
    }
    return column.label?.fullTextLabel || column.label?.toString() || ''
}

function getManageColumnDisplayLabel (column: WidgetTemplateColumnWithConfig): string {
    if (labelType.value === tableHeaderGlobalOptions.value.shortText.label) {
        return column.label?.fullTextLabel || column.label?.toString() || ''
    }
    return getColumnDisplayLabel(column)
}

function isEditingColumn (columnProp: string): boolean {
    return editingColumnProp.value === columnProp
}

function hasCustomLabel (columnProp: string): boolean {
    const config = columnsConfigRef.value[columnProp]
    return !!config?.customTextLabel
}

function resetColumnLabel (columnProp: string) {
    const config = columnsConfigRef.value[columnProp]
    if (config) {
        const { customTextLabel, ...configWithoutCustom } = config
        columnsConfigRef.value[columnProp] = configWithoutCustom

        if (editingColumnValues[columnProp]) {
            delete (editingColumnValues as Record<string, string>)[columnProp]
        }

        updateColumnConfig()
    }
}

function setColumnInputRef (columnProp: string, el: ComponentPublicInstance | null) {
    if (el) {
        columnInputRefs.value[columnProp] = el
    }
}

function getTempEditingValue (columnProp: string): string {
    return tempEditingValues.value[columnProp] || ''
}

function startEditingColumn (column: WidgetTemplateColumnWithConfig) {
    const config = columnsConfigRef.value[column.prop]
    const currentValue = config?.customTextLabel || column.label?.fullTextLabel || ''
    editingColumnValues[column.prop] = currentValue
    tempEditingValues.value = {
        ...tempEditingValues.value,
        [column.prop]: currentValue
    }
    originalColumnValues[column.prop] = currentValue
    editingColumnProp.value = column.prop
    nextTick(() => {
        const inputRef = columnInputRefs.value[column.prop]
        if (inputRef) {
            const elInput = (inputRef as any).$refs?.inputRef
            if (elInput) {
                const input = elInput.$el || elInput
                input?.focus()
                input?.select()
            }
        }
    })
}

function saveColumnLabel (column: WidgetTemplateColumnWithConfig) {
    const trimmedValue = (tempEditingValues.value[column.prop] as string | undefined)?.trim()

    if (trimmedValue) {
        editingColumnValues[column.prop] = trimmedValue
    } else {
        editingColumnValues[column.prop] = originalColumnValues[column.prop] || ''
    }

    if (trimmedValue) {
        columnsConfigRef.value[column.prop] = {
            ...columnsConfigRef.value[column.prop],
            showShortTextLabel: false,
            showFullTextLabel: false,
            customTextLabel: trimmedValue
        }
    } else {
        const { customTextLabel, ...configWithoutCustom } = columnsConfigRef.value[column.prop]
        columnsConfigRef.value[column.prop] = configWithoutCustom
    }
    updateColumnConfig()
    editingColumnProp.value = null
    delete (editingColumnValues as Record<string, string>)[column.prop]
    const { [column.prop]: _, ...rest } = tempEditingValues.value
    tempEditingValues.value = rest
    delete (originalColumnValues as Record<string, string>)[column.prop]
}

function cancelEditingColumn (columnProp: string) {
    editingColumnProp.value = null
    delete (editingColumnValues as Record<string, string>)[columnProp]
    const { [columnProp]: _, ...rest } = tempEditingValues.value
    tempEditingValues.value = rest
    delete (originalColumnValues as Record<string, string>)[columnProp]
}

//Methods
function selectAllColumns (state?: string | number | boolean) {
    if (state) {
        showAll()
    } else {
        hideAll()
    }
}

function addToHidden (columnProp: string) {
    if (columnsConfigRef.value[columnProp]) {
        columnsConfigRef.value[columnProp].hidden = true
        updateColumnConfig()
    }
}

function addToVisible (columnProp: string) {
    if (columnsConfigRef.value[columnProp]) {
        columnsConfigRef.value[columnProp].hidden = false
        updateColumnConfig()
    }
}

function updateEditingValue (columnProp: string, value: string | number | undefined) {
    const stringValue = value !== undefined && value !== null ? String(value) : ''
    tempEditingValues.value = {
        ...tempEditingValues.value,
        [columnProp]: stringValue
    }
}

const hideAll = () => {
    Object.keys(columnsConfigRef.value).forEach((key) => {
        columnsConfigRef.value[key].hidden = true
    })
    updateColumnConfig()
}

const showAll = () => {
    Object.keys(columnsConfigRef.value).forEach((key) => {
        columnsConfigRef.value[key].hidden = false
    })
    updateColumnConfig()
}

const updateColumnConfig = () => {
    emit('update-columns-config', columnsConfigRef.value)
}

const regenerateColumnsConfig = () => {
    emit('regenerate-columns-config')
}

const onEnd = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
    if (oldIndex === newIndex) return

    visibleColumnsRef.value.forEach((column, i) => {
        columnsConfigRef.value[column.prop].order = i
    })
    updateColumnConfig()
}

const saveEditingColumnLabel = () => {
    if (editingColumnProp.value) {
        const column = visibleColumnsRef.value.find(c => c.prop === editingColumnProp.value)
        if (column) {
            saveColumnLabel(column)
        }
    }
}

defineExpose({
    saveEditingColumnLabel
})

watch(() => props.columnsConfig, (value) => {
    columnsConfigRef.value = cloneDeep(value)
}, {
    immediate: true,
    deep: true
})

watch(() => props.visibleColumns, (value) => {
    visibleColumnsRef.value = cloneDeep(value)
}, {
    deep: true,
    immediate: true
})

watch(() => iconsVisibility.value, (val) => {
    if (isSyncingGlobalHeadersConfig.value) return
    emit('change-icons-visibility', val)
})

watch(() => labelType.value, (value, oldValue) => {
    if (isSyncingGlobalHeadersConfig.value) return
    if (oldValue !== tableHeaderGlobalOptions.value.custom.label && value === tableHeaderGlobalOptions.value.custom.label)
        iconsVisibility.value = false
    if (oldValue === tableHeaderGlobalOptions.value.custom.label && value !== tableHeaderGlobalOptions.value.custom.label && editingColumnProp.value) {
        cancelEditingColumn(editingColumnProp.value)
    }
    if (value !== oldValue) {
        emit('change-label', value)
    }
})

watch(() => props.globalHeadersConfig, (val) => {
    if (Object.values(val)?.length) {
        isSyncingGlobalHeadersConfig.value = true
        labelType.value = val.labelTextType
        iconsVisibility.value = val.showAllIcons
        nextTick(() => {
            isSyncingGlobalHeadersConfig.value = false
        })
    }
}, { immediate: true })
</script>

<style lang="scss">
.redesign-columns-manage {
    background: var(--mainBgColor-color);
    display: flex;
    flex-direction: column;
    flex: 1;

    .manage-block {
        @apply px-4 mb-4 py-4;
        overflow-y: auto;
    }
}

.column-item {
    @apply p-2 flex justify-between items-center mb-2;
    border: 1px solid var(--gray-150);
    border-radius: 4px;

    &--active {
        border: 1px solid var(--primary-actions);
    }

    &--draggable {
        cursor: pointer;
    }

    .el-checkbox {
        @apply w-full;
    }

    .el-checkbox__label {
        @apply truncate;
    }

    .el-checkbox:hover {
        .el-checkbox__input:not(.is-disabled) .el-checkbox__inner {
            border-color: var(--primary-color);
        }
    }

    .column-edit-btn {
        opacity: 0;
        transition: opacity 0.2s;
    }

    &:hover .column-edit-btn {
        opacity: 1;
    }

    .column-edit-input {
        min-width: 120px;
        max-width: 150px;
        flex: 1;
    }
}
</style>

